import { useCallback, useState, useMemo } from 'react';
import { useStoredFollowingCount } from '~/common/hooks/use-auth/use-stored-following-count';
import { useTellerSession } from '~/common/hooks/use-auth/use-teller-session';
import { useFirebaseAnalytics } from '~/common/hooks/use-firebase-analytics';
import type { FollowButtonState } from '@app/_components/follow-unfollow-button';
import { useFetchUserFollowStatus } from '@app/_hooks/use-fetch-user-follow-status';
import { useFollow, useUnfollow } from '@app/_hooks/use-follow-unfollow';
import { useSystemNotification } from '@app/_hooks/use-system-notification';

import type { Props } from './types';

type Params = Pick<
  Props,
  | 'user'
  | 'buttonPlacementLocation'
  | 'onFollow'
  | 'onUnfollow'
  | 'showFollowingTextInsteadOfButton'
  | 'onLoginNeededOnFollow'
>;

export const useLocalHooks = ({
  user,
  buttonPlacementLocation,
  onFollow,
  onUnfollow,
  showFollowingTextInsteadOfButton,
}: Params) => {
  const userId = user.id;
  const { session } = useTellerSession();

  const { update: followUpdate } = useFollow();
  const { update: unfollowUpdate } = useUnfollow();
  const followStatus = useFetchUserFollowStatus(userId);
  const [isShowConfirm, setShowConfirm] = useState(false);

  const [isShowLoginRequiredModal, setShowLoginRequiredModal] = useState(false);

  const { logUserFollow, logUserUnfollow } = useFirebaseAnalytics();

  const { notify } = useSystemNotification();

  const eventPage = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return window.location.pathname;
  }, []);
  const eventParentPage = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return location.pathname.replace(/\/[^/]*$/, '');
  }, []);

  const { addFollowingCount, substractFollowingCount } =
    useStoredFollowingCount();

  const follow = useCallback(async () => {
    return new Promise<void>((resolve, reject) => {
      if (!userId || session?.isAnonymous) {
        reject();
        return;
      }
      followUpdate({ userId })
        .then(() => {
          logUserFollow(
            eventPage,
            eventParentPage,
            userId,
            buttonPlacementLocation
          );
          addFollowingCount();
          if (onFollow) {
            onFollow();
          }
          resolve();
        })
        .catch((error) => {
          if (error.message) {
            notify(error.message, 'error');
          }
        });
    });
  }, [
    userId,
    session?.isAnonymous,
    followUpdate,
    logUserFollow,
    eventPage,
    eventParentPage,
    buttonPlacementLocation,
    addFollowingCount,
    onFollow,
    notify,
  ]);

  const unfollow = useCallback(async () => {
    return new Promise<void>((resolve, reject) => {
      if (!userId) {
        reject();
        return;
      }
      unfollowUpdate({
        userId,
      }).then(() => {
        if (session?.uid) {
          logUserUnfollow(
            eventPage,
            eventParentPage,
            userId,
            buttonPlacementLocation
          );
        }

        substractFollowingCount();
        if (onUnfollow) {
          onUnfollow();
        }
        resolve();
      });
    });
  }, [
    session?.uid,
    buttonPlacementLocation,
    eventPage,
    eventParentPage,
    logUserUnfollow,
    onUnfollow,
    substractFollowingCount,
    unfollowUpdate,
    userId,
  ]);

  const showConfirm = useCallback(() => {
    setShowConfirm(true);
  }, []);

  const closeConfirm = useCallback(() => {
    setShowConfirm(false);
  }, []);

  const closeConfirmAndUnfollow = useCallback(() => {
    unfollow();
    closeConfirm();
  }, [closeConfirm, unfollow]);

  const isFollowedByMe = followStatus?.value?.isFollowedByMe;
  const isFollowingMe = followStatus?.value?.isFollowingMe;
  const tellerUser = session?.tellerUser;

  const handleOnClick = useCallback(() => {
    if (isFollowedByMe && !showFollowingTextInsteadOfButton) {
      showConfirm();
    } else {
      if (tellerUser && !session.isAnonymous) {
        follow();
      } else {
        setShowLoginRequiredModal(true);
      }
    }
  }, [
    follow,
    isFollowedByMe,
    session?.isAnonymous,
    showConfirm,
    showFollowingTextInsteadOfButton,
    tellerUser,
  ]);

  const followState = useMemo((): FollowButtonState => {
    if (isFollowedByMe) {
      return 'followed-by-me';
    } else if (isFollowingMe) {
      return 'follow-back';
    } else {
      return 'not-followed-by-me';
    }
  }, [isFollowedByMe, isFollowingMe]);

  const canUnfollow = useMemo(() => {
    return isFollowedByMe && !showFollowingTextInsteadOfButton;
  }, [isFollowedByMe, showFollowingTextInsteadOfButton]);

  const closeShowLoginRequiredModal = useCallback(() => {
    setShowLoginRequiredModal(false);
  }, []);

  const isMe = useMemo(() => {
    if (!session) return;
    return session?.uid === userId;
  }, [session, userId]);

  return {
    handleOnClick,
    followState,
    canUnfollow,
    isShowConfirm,
    user,
    closeConfirm,
    closeConfirmAndUnfollow,
    isShowLoginRequiredModal,
    closeShowLoginRequiredModal,
    isMe,
  };
};
